import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import { intersection, includes } from 'lodash';
import { FormHelpText, Type, TypeList, TypeListItem } from '@spotify-internal/encore-creator-web';
import colors from '@spotify-internal/encore-foundation';
import styled from 'styled-components';
import { PODCAST_SUPPORT_EMAIL } from '../../../lib/constants';
import { injectIntl, IntlShape, FormattedMessage, FormattedDate } from 'react-intl';
import { TSFix } from '@spotify-internal/ts-migration-utils/types';

const MailLink = styled.a`
  color: ${colors.azure};
  font-weight: 600;
  text-decoration: underline;
  &:hover {
    color: black;
  }
`;

const GENERAL_ERRORS = {
  DOWNLOAD_FAILURE: 'DOWNLOAD_FAILURE',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
  NEEDS_APPROVAL: 'NEEDS_APPROVAL',
  INVALID_XML: 'INVALID_XML',
  VIDEO_PODCAST: 'VIDEO_PODCAST',
  UNSUPPORTED_EMAIL: 'UNSUPPORTED_EMAIL',
  UNSUPPORTED_EMAIL_ANCHOR: 'UNSUPPORTED_EMAIL_ANCHOR',
  UNSUPPORTED_EMAIL_SOUNDCLOUD: 'UNSUPPORTED_EMAIL_SOUNDCLOUD',
  PATREON_FEED: 'PATREON_FEED',
};

const MISSING_ERRORS = {
  MISSING_IMAGE: 'MISSING_IMAGE',
  MISSING_DESCRIPTION: 'MISSING_DESCRIPTION',
  MISSING_TITLE: 'MISSING_TITLE',
  MISSING_EPISODES: 'MISSING_EPISODES',
  MISSING_AUTHOR: 'MISSING_AUTHOR',
  MISSING_GUID: 'MISSING_GUID',
  MISSING_EMAIL: 'MISSING_EMAIL',
};

const errorMessages = {
  [GENERAL_ERRORS.DOWNLOAD_FAILURE]: (
    <FormattedMessage
      id="add-podcast.rss-verification-download-error"
      defaultMessage="That URL doesn’t look right. Make sure you typed it correctly."
    />
  ),
  [GENERAL_ERRORS.UNKNOWN_ERROR]: (
    <FormattedMessage
      id="add-podcast.rss-verification-unknown-error"
      defaultMessage="Something has gone wrong. Please try again"
    />
  ),
  [GENERAL_ERRORS.NEEDS_APPROVAL]: (
    <FormattedMessage
      id="add-podcast.rss-verification-needs-approval"
      defaultMessage="You’re a VIP! To get this podcast on Spotify, you need to
               <mail>contact our team</mail>
                for help."
      values={{
        mail: (...chunks) => <MailLink href={`mailto:${PODCAST_SUPPORT_EMAIL}`}>{chunks}</MailLink>,
      }}
    />
  ),
  [GENERAL_ERRORS.INVALID_XML]: (
    <FormattedMessage
      id="add-podcast.rss-verification-invalid-error"
      defaultMessage="Your RSS feed failed to load. Make sure you added the right link."
    />
  ),
  [GENERAL_ERRORS.VIDEO_PODCAST]: (
    <FormattedMessage
      id="add-podcast.rss-verification-video-error"
      defaultMessage="We’re unable to accept podcasts with videos. <a>Learn more</a>"
      values={{
        a: (...chunks) => {
          <a href="/faq#can-i-have-video-episodes-in-my-rss-feed">{chunks}</a>;
        },
      }}
    />
  ),
  [GENERAL_ERRORS.UNSUPPORTED_EMAIL]: (
    <FormattedMessage
      id="add-podcast.rss-verification-unsupported-email-error"
      defaultMessage="The email address connected to that feed belongs to your hosting provider. Ask them to update it to an email address you own."
    />
  ),
  [GENERAL_ERRORS.UNSUPPORTED_EMAIL_ANCHOR]: (
    <FormattedMessage
      id="add-podcast.rss-verification-unsupported-anchor-email-error"
      defaultMessage="The email address in your RSS feed belongs to Anchor. {linebreak}
        Please visit <link>this link</link> for more info about how to update this email to an address you own."
      values={{
        linebreak: <br />,
        link: (...chunks) => <MailLink href="https://anch.co/anchorS4Phelp">{chunks}</MailLink>,
      }}
    />
  ),
  [GENERAL_ERRORS.UNSUPPORTED_EMAIL_SOUNDCLOUD]: (
    <FormattedMessage
      id="add-podcast.rss-verification-unsupported-soundcloud-email-error"
      defaultMessage="The email address in your RSS feed belongs to Soundcloud. {linebreak}
        Visit <link>help.soundcloud.com</link> to update it to an email address you own"
      values={{
        linebreak: <br />,
        link: (...chunks) => (
          <MailLink href="https://help.soundcloud.com/hc/en-us/articles/115003447807-Displaying-an-email-address-in-your-RSS-feed">
            {chunks}
          </MailLink>
        ),
      }}
    />
  ),
  [GENERAL_ERRORS.PATREON_FEED]: (
    <FormattedMessage
      id="add-podcast.rss-verification-patreon-error"
      defaultMessage="Currently, it’s not possible to add a private podcast RSS feed from Patreon to Spotify.{linebreak}
        To listen, <a>try these apps instead</a>."
      values={{
        linebreak: <br />,
        a: (...chunks) => (
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://support.patreon.com/hc/en-us/articles/115005429546-Will-my-podcast-benefits-work-with-my-favorite-podcast-app-"
          >
            {chunks}
          </a>
        ),
      }}
    />
  ),

  [MISSING_ERRORS.MISSING_IMAGE]: (
    <FormattedMessage
      id="add-podcast.rss-verification-missing-cover-art"
      description="Part of the error message when the podcast is missing cover art"
      defaultMessage="Cover art"
    />
  ),
  [MISSING_ERRORS.MISSING_DESCRIPTION]: (
    <FormattedMessage
      id="add-podcast.rss-verification-missing-description"
      description="Part of the error message when the podcast is missing a description"
      defaultMessage="Description"
    />
  ),
  [MISSING_ERRORS.MISSING_TITLE]: (
    <FormattedMessage
      id="add-podcast.rss-verification-missing-title"
      description="Part of the error message when the podcast is missing a title"
      defaultMessage="Title"
    />
  ),
  [MISSING_ERRORS.MISSING_EPISODES]: (
    <FormattedMessage
      id="add-podcast.rss-verification-missing-episodes"
      description="Part of the error message when the podcast has no episodes"
      defaultMessage="Episodes"
    />
  ),
  [MISSING_ERRORS.MISSING_AUTHOR]: (
    <FormattedMessage
      id="add-podcast.rss-verification-missing-author"
      description="Part of the error message when the podcast is missing an author"
      defaultMessage="Author"
    />
  ),
  [MISSING_ERRORS.MISSING_EMAIL]: (
    <FormattedMessage
      id="add-podcast.rss-verification-missing-email-address"
      description="Part of the error message when the podcast is missing an email address"
      defaultMessage="Email address"
    />
  ),
  [MISSING_ERRORS.MISSING_GUID]: (
    <FormattedMessage
      id="add-podcast.rss-verification-missing-guid"
      description="Part of the error message when the podcast is missing a Globally Unique Identifier"
      defaultMessage="Your podcast needs a globally unique identifier (GUID). <a>Learn more</a>."
      values={{
        a: (...chunks) => <a href="/faq#whats-a-globally-unique-identifier-guid">{chunks}</a>,
      }}
    />
  ),
};

const ErrorContainer = styled.div`
  margin-top: 16px;
`;

const StyledTypeListItem = styled(TypeListItem)`
  font-size: 14px;
`;

interface Props {
  errors: string[];
}

export class RSSVerificationErrorFeedback extends PureComponent<Props> {
  static propTypes = {
    errors: PropTypes.arrayOf(PropTypes.string),
  };

  get isGeneralError() {
    return intersection(this.props.errors, Object.values(GENERAL_ERRORS)).length > 0;
  }

  generalErrorTemplate = errors => (
    <Fragment>
      {errors.map(error => (
        <Type
          as="p"
          data-qa="download-failure"
          color={error === GENERAL_ERRORS.NEEDS_APPROVAL ? 'azure' : 'failure'}
          variant={Type.sm}
          key={error}
        >
          {errorMessages[GENERAL_ERRORS[error]]}
        </Type>
      ))}
      {includes(errors, GENERAL_ERRORS.INVALID_XML) && (
        <FormHelpText>
          <FormattedMessage
            id="add-podcast.rss-verification-error"
            description=""
            defaultMessage="Need help? Try looking in the Help section of your podcast aggregator/host."
          />
        </FormHelpText>
      )}
    </Fragment>
  );

  missingInfoErrorTemplate = errors => (
    <Fragment>
      <FormHelpText error>
        Your podcast RSS feed is missing some things:
        <TypeList.ul data-qa="error-list">
          {errors.map(errorType => (
            <StyledTypeListItem condensed key={errorType}>
              {errorMessages[errorType]}
            </StyledTypeListItem>
          ))}
        </TypeList.ul>
      </FormHelpText>
      <FormHelpText>
        <FormattedMessage
          id="add-podcast.rss-verification-missing-info"
          description=""
          defaultMessage="Need help? Try looking in the Help section of your podcast aggregator/host."
        />
      </FormHelpText>
    </Fragment>
  );

  render() {
    const errorMessage = this.isGeneralError
      ? this.generalErrorTemplate(this.props.errors)
      : this.missingInfoErrorTemplate(this.props.errors);

    return <ErrorContainer>{errorMessage}</ErrorContainer>;
  }
}
